import { useMemo, useState } from 'react';
import { Check, Pin, PinOff, Eye, Loader2 } from 'lucide-react';
import { shortTime, initials } from '../lib/format.js';
import { useWorkspace } from '../lib/workspace.jsx';

/**
 * Single-line conversation rows — the density the design is built around.
 * Grouped by whether the row is still waiting on an agent, because that is the
 * only distinction that changes what you do next — with anything the agent has
 * pinned lifted above both, since a pin is them saying so explicitly.
 */
function Row({
  conversation,
  active,
  color,
  appName,
  assignee,
  pinned,
  pinnedByName,
  canPin,
  viewers,
  onSelect,
  onTogglePin,
}) {
  const unread = conversation.unread.agent;
  const urgent = conversation.hasEmergency && !['resolved', 'closed'].includes(conversation.status);
  const resolved = ['resolved', 'closed'].includes(conversation.status);

  /* Somebody else has this thread open. The strongest of the two states wins:
     typing means a reply is being written right now. */
  const occupant = viewers?.find((v) => v.typing) ?? viewers?.[0] ?? null;

  return (
    /* The pin is a sibling of the row, not a child of it: a button inside a
       button is invalid, and browsers resolve it by making the outer one
       unclickable in exactly the places you most want to click. */
    <div className={`row-wrap${pinned ? ' is-pinned' : ''}`}>
    <button
      type="button"
      className={`row${active ? ' is-active' : ''}${unread ? ' is-unread' : ''}`}
      onClick={() => onSelect(conversation.id)}
    >
      <span
        className="row__dot"
        style={{
          background: urgent ? 'var(--danger)' : unread ? 'var(--accent)' : 'transparent',
        }}
      />
      <span className="row__stripe" style={{ background: color }} />
      {/* Which tenant, when the list is several of them at once. The stripe
          already carries the colour; this is the half you can read. */}
      {appName && (
        <span className="row__app" style={{ color }} title={appName}>
          {appName}
        </span>
      )}
      {/* A renamed chat shows its title here — that is the point of renaming. */}
      <span className="row__name">
        {conversation.subject || conversation.visitor.name || conversation.visitor.visitorId}
      </span>
      <span className="row__snippet">{conversation.lastMessagePreview || 'No messages yet'}</span>

      {/* A colleague is already in here. Said on the row rather than only once
          the thread is open, because the useful moment to learn it is before
          two people have both started typing the same answer. */}
      {occupant && (
        <span
          className={`row__viewer${occupant.typing ? ' is-typing' : ''}`}
          title={`${occupant.name || 'Another agent'} ${
            occupant.typing ? 'is typing here' : 'has this conversation open'
          }`}
        >
          <Eye size={11} />
        </span>
      )}

      {/* Who owns this thread, at a glance. Without it the only way to tell an
          assigned row from an unassigned one is to open it. */}
      {assignee && (
        <span className="row__assignee" title={`Assigned to ${assignee}`}>
          {initials(assignee)}
        </span>
      )}

      {urgent ? (
        <span className="flag flag--danger">SOS</span>
      ) : unread ? (
        <span className="flag flag--count">{unread}</span>
      ) : resolved ? (
        <Check size={12} className="row__check" />
      ) : null}

      <span className="row__time">{shortTime(conversation.lastMessageAt)}</span>
    </button>

    {/* Hidden until hover unless it is on — an inbox of forty rows should not
        be forty pin icons. Pinned rows keep it visible, because that is the
        control that undoes the state you can see.

        A read-only member sees the pin but cannot move it: the state is a fact
        about the thread they are entitled to read, and the server would refuse
        the write anyway. */}
    <button
      type="button"
      className={`row__pin${pinned ? ' is-on' : ''}`}
      onClick={() => onTogglePin(conversation.id)}
      disabled={!canPin}
      aria-pressed={pinned}
      title={
        pinned
          ? `Pinned for the team${pinnedByName ? ` by ${pinnedByName}` : ''}${
              canPin ? ' — unpin for everyone' : ''
            }`
          : canPin
            ? 'Pin to the top of the inbox for everyone on this app'
            : 'Pinning needs reply access'
      }
      aria-label={pinned ? 'Unpin this conversation' : 'Pin this conversation'}
    >
      {pinned ? <PinOff size={11} /> : <Pin size={11} />}
    </button>
    </div>
  );
}

/** Rows revealed per "load more". */
const PAGE_SIZE = 50;

/**
 * The foot of the list: reveal the next page of what is already loaded, and
 * once that runs out, fetch older conversations from the server. One button for
 * both, because to the agent they are the same request — "show me more".
 */
function LoadMore({ shown, total, hasMore, loading, onClick }) {
  if (shown >= total && !hasMore) return null;
  return (
    <div className="inbox__more">
      {total > 0 && (
        <span className="inbox__more-count">
          {shown} of {total}
          {hasMore ? '+' : ''}
        </span>
      )}
      <button type="button" className="inbox__more-button" onClick={onClick} disabled={loading}>
        {loading ? (
          <>
            <Loader2 size={12} className="spin" /> Loading…
          </>
        ) : shown < total ? (
          'Load more'
        ) : (
          'Load older conversations'
        )}
      </button>
    </div>
  );
}

/**
 * Paginated in every tab. The parent remounts this (via `key`) when the tab,
 * search or scope changes, which is what puts the list back on its first page.
 */
export function Inbox({ conversations, loading, activeId, onSelect }) {
  const {
    appIndex,
    allApps,
    members,
    isPinned,
    toggleConversationPin,
    viewersOf,
    canOn,
    hasMoreConversations,
    loadingMoreConversations,
    loadMoreConversations,
  } = useWorkspace();
  const [limit, setLimit] = useState(PAGE_SIZE);

  const memberNames = useMemo(
    () =>
      Object.fromEntries(members.map((m) => [m.userId, m.name || m.email])),
    [members]
  );

  const groups = useMemo(() => {
    /**
     * Pinned first, and out of the other two groups entirely.
     *
     * A pinned thread that is also unread would otherwise appear twice, and
     * splitting the pin across two headings defeats the point of it: an agent
     * pins a conversation so there is exactly one place to look for it.
     */
    const pinned = conversations.filter((c) => isPinned(c));
    const rest0 = conversations.filter((c) => !isPinned(c));
    const waiting = rest0.filter(
      (c) => c.unread.agent > 0 || (c.hasEmergency && c.status === 'open')
    );
    const rest = rest0.filter((c) => !waiting.includes(c));

    // The page is cut from the grouped order, not the raw one, so pinned and
    // waiting rows are always on the first page wherever their timestamps sit.
    let budget = limit;
    return [
      { label: 'PINNED', items: pinned },
      { label: 'LIVE · WAITING ON YOU', items: waiting },
      { label: 'EVERYTHING ELSE', items: rest },
    ]
      .map((group) => {
        const items = group.items.slice(0, Math.max(budget, 0));
        budget -= items.length;
        return { ...group, items };
      })
      .filter((group) => group.items.length > 0);
  }, [conversations, isPinned, limit]);

  const shown = Math.min(limit, conversations.length);

  const showMore = () => {
    setLimit((current) => current + PAGE_SIZE);
    // Everything loaded is already on screen (or will be after this page), so
    // the next rows have to come from the server.
    if (limit + PAGE_SIZE > conversations.length && hasMoreConversations) {
      loadMoreConversations();
    }
  };

  const footer = (
    <LoadMore
      shown={shown}
      total={conversations.length}
      hasMore={hasMoreConversations}
      loading={loadingMoreConversations}
      onClick={showMore}
    />
  );

  if (loading) return <div className="inbox__empty">Loading…</div>;
  if (conversations.length === 0) {
    return (
      <div className="inbox__list">
        <div className="inbox__empty">
          No conversations match this filter
          {hasMoreConversations ? ' among those loaded so far.' : '.'}
        </div>
        {footer}
      </div>
    );
  }

  return (
    <div className="inbox__list">
      {groups.map((group) => (
        <div key={group.label}>
          <div className="inbox__group">{group.label}</div>
          {group.items.map((conversation) => (
            <Row
              key={conversation.id}
              conversation={conversation}
              active={conversation.id === activeId}
              // Each row is coloured by the app it actually belongs to, which
              // is what makes a merged inbox readable at a glance.
              color={appIndex[conversation.appId]?.color ?? 'var(--accent)'}
              appName={allApps ? (appIndex[conversation.appId]?.name ?? null) : null}
              assignee={memberNames[conversation.assigneeId] ?? null}
              pinned={isPinned(conversation)}
              pinnedByName={memberNames[conversation.pinnedBy] ?? null}
              // Per row, not per workspace: in a merged inbox the grant that
              // applies is the one on the app the row itself belongs to.
              canPin={canOn(conversation.appId, 'chat', 'send')}
              viewers={viewersOf(conversation.id)}
              onSelect={onSelect}
              onTogglePin={toggleConversationPin}
            />
          ))}
        </div>
      ))}
      {footer}
    </div>
  );
}

export default Inbox;
